import { InfoBox } from '@/src/components/InfoBox'
import { DestructiveButton } from '@/src/components/buttons/DestructiveButton'
import { SaveButton } from '@/src/components/buttons/SaveButton'
import { Header } from '@/src/components/headers/Header'
import CancelButton from '@/src/components/shared/buttons/CancelButton'
import { ALL_LOCATIONS, useLocationsContext } from '@/src/contexts/LocationsContext'
import { useCreateLoadPlanningAdjustmentForm } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/hooks/useCreateLoadPlanningAdjustmentForm.hook'
import { useLoadPlanningPreview } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/hooks/useLoadPlanningPreview.hook'
import { LoadPlanningPreviewVisuals } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/preview-graph/LoadPlanningPreviewVisuals'
import {
  Box,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'
import { useLoadPlanning } from '@features/procurement-and-markets/hooks/useLoadPlanningsV2.hook'
import type { ProcurementEnergyType } from '@features/procurement-and-markets/load-planning/LoadPlanning.types'
import { useLoadPlanningAdjustments } from '@features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import { useLoadedStrategyMetadata } from '@features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import { LoadAdjustmentFormTypeEnum } from '@features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.types'
import LoadPlanningAdjustmentForm from '@features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustmentForm'
import {
  getOverlappingHolidayTimeframes,
  hasOverlappingCompanyReferenceCorrection,
} from '@features/procurement-and-markets/load-planning/load-planning-adjustments/table/adjustmentTable.utils'
import { getLoadPlanningEnergyTypeLabel } from '@features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatOverlappingHolidayTimeframesLabel } from './LoadPlanningMeasureModal/LoadPlanningMeasureModalInfoSection.utils'
import type { AdjustmentRow } from './table/adjustmentTable.types'

interface ForecastCreationModalV2Props {
  isOpen: boolean
  onClose: () => void
  adjustment?: AdjustmentRow
  onDelete?: () => void
}

const LoadPlanningAdjustmentModal = ({ isOpen, onClose, adjustment, onDelete }: ForecastCreationModalV2Props) => {
  const { t, i18n } = useTranslation()
  const { companyPgId: companyId } = useLocationsContext()
  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const loadPlanningYear = Number(strategyMetadata?.year)
  const { formik, isEditing } = useCreateLoadPlanningAdjustmentForm(adjustment, loadPlanningYear, onClose)
  const { onCalculate, isCalculated, dataToCalculate } = useLoadPlanningPreview(formik)

  const isCompanyScoped = formik.values.locationId === ALL_LOCATIONS
  const selectedLocationId = formik.values.locationId && !isCompanyScoped ? Number(formik.values.locationId) : undefined

  const { data: adjustments } = useLoadPlanningAdjustments({
    companyId,
    locationId: selectedLocationId,
    energyType: strategyMetadata?.energyType as ProcurementEnergyType,
    year: loadPlanningYear,
  })

  const { data: loadPlanning } = useLoadPlanning(companyId, loadPlanningYear, strategyMetadata?.energyType)
  const hasCustomReferenceBaseLoad = !!loadPlanning?.hasCustomReferenceBaseLoad

  const isReferencePeriodType = formik.values.type === LoadAdjustmentFormTypeEnum.REFERENCE_PERIOD

  const referencePeriodIgnoredReason = useMemo<'custom_reference' | 'company_overlap' | null>(() => {
    if (!isReferencePeriodType) return null

    if (hasCustomReferenceBaseLoad) return 'custom_reference'

    if (isCompanyScoped || !adjustments || !formik.values.timeframeStart || !formik.values.timeframeEnd) return null

    const isOverriddenByCompanyCorrection = hasOverlappingCompanyReferenceCorrection(
      {
        id: formik.values.id ?? '__current_adjustment__',
        scope: 'LOCATION',
        locationId: selectedLocationId,
        timeframeStart: formik.values.timeframeStart,
        timeframeEnd: formik.values.timeframeEnd,
      },
      adjustments
    )

    return isOverriddenByCompanyCorrection ? 'company_overlap' : null
  }, [
    isReferencePeriodType,
    hasCustomReferenceBaseLoad,
    isCompanyScoped,
    adjustments,
    formik.values.id,
    formik.values.timeframeStart,
    formik.values.timeframeEnd,
    selectedLocationId,
  ])

  const overlappingHolidayTimeframes = useMemo(() => {
    if (!adjustments || !formik.values.timeframeStart || !formik.values.timeframeEnd) return []

    return getOverlappingHolidayTimeframes(
      {
        id: formik.values.id ?? '__current_adjustment__',
        scope: isCompanyScoped ? 'COMPANY' : 'LOCATION',
        locationId: selectedLocationId,
        timeframeStart: formik.values.timeframeStart,
        timeframeEnd: formik.values.timeframeEnd,
      },
      adjustments,
      i18n.language
    )
  }, [
    adjustments,
    formik.values.id,
    isCompanyScoped,
    selectedLocationId,
    formik.values.timeframeEnd,
    formik.values.timeframeStart,
    i18n.language,
  ])

  const overlappingHolidayTimeframesLabel = useMemo(
    () => formatOverlappingHolidayTimeframesLabel(overlappingHolidayTimeframes, i18n.language),
    [overlappingHolidayTimeframes, i18n.language]
  )

  useEffect(() => {
    if (adjustment) {
      onCalculate()
    }
  }, [])

  return (
    <Modal size='6xl' motionPreset='slideInBottom' isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />

      <ModalContent maxWidth='1400px'>
        <ModalHeader>{`${t(isEditing ? 'components.procurement.forecasting.modal.form.headingEdit' : 'components.procurement.forecasting.modal.form.heading', { year: loadPlanningYear, energyType: getLoadPlanningEnergyTypeLabel(t, strategyMetadata?.energyType) })}`}</ModalHeader>
        <ModalCloseButton _focus={{}} data-action='loadplan-adjustmentmodal-closex' />

        <ModalBody>
          {overlappingHolidayTimeframes.length > 0 && (
            <Box marginBottom={'12px'} marginTop={'-16px'}>
              <InfoBox
                variant='amberLight'
                description={t(
                  isCompanyScoped
                    ? 'components.procurement.forecasting.measure_modal.holiday_overlap_info_company'
                    : 'components.procurement.forecasting.measure_modal.holiday_overlap_info',
                  {
                    timeframes: overlappingHolidayTimeframesLabel,
                  }
                )}
              />
            </Box>
          )}
          {referencePeriodIgnoredReason && (
            <Box marginBottom={'12px'} marginTop={'-16px'}>
              <InfoBox
                variant='amberLight'
                description={t(
                  referencePeriodIgnoredReason === 'custom_reference'
                    ? 'components.procurement.forecasting.measure_modal.reference_period_ignored_custom_reference'
                    : 'components.procurement.forecasting.measure_modal.reference_period_ignored_company_overlap'
                )}
              />
            </Box>
          )}
          <Flex>
            <Box flex={1} borderRight={'1px solid'} borderColor={'border'} pr='12px'>
              <Header title={t('components.procurement.forecasting.modal.form.parameter_heading')} variant={'H3'} />
              <Box marginBottom={'12px'} />
              <LoadPlanningAdjustmentForm isEditing={isEditing} formik={formik} onCalculate={onCalculate} year={loadPlanningYear} />
            </Box>
            <Box flex={3} borderColor={'border'} pr='12px' pl='12px'>
              <Header title={t('components.procurement.forecasting.modal.form.result_heading')} variant={'H3'} />
              <Box marginBottom={'12px'} />
              <LoadPlanningPreviewVisuals dataToPreview={dataToCalculate} />
            </Box>
          </Flex>
        </ModalBody>

        <Divider height='1px' mt={'10px'} />

        <ModalFooter justifyContent={isEditing ? 'space-between' : 'end'}>
          {isEditing && onDelete && (
            <DestructiveButton text={t('components.procurement.forecasting.modal.form.delete_button_text')} onClick={onDelete} />
          )}
          <Flex gap='10px'>
            <CancelButton onClose={onClose} dataAction='loadplan-adjustmentmodal-cancel-action' />
            <SaveButton
              data-action='loadplan-adjustmentmodal-save-action'
              isDisabled={!formik.isValid || !formik.dirty || !isCalculated}
              isLoading={formik.isSubmitting}
              text={
                isEditing
                  ? t('components.procurement.forecasting.modal.form.edit_button_text')
                  : t('components.procurement.forecasting.modal.form.save_button_text')
              }
              onClick={() => formik.handleSubmit()}
            />
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default LoadPlanningAdjustmentModal
